import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getSummary, markRead as apiMarkRead, markAllRead as apiMarkAllRead, markReadByEntity as apiMarkReadByEntity, markStoreAlertGroupRead as apiMarkStoreAlertGroupRead } from "../services/notificationsService";
import { isStoreAlert, STORE_ALERT_ENTITY_TYPES } from "../utils/notificationsDisplay";

/**
 * THE APP'S LARGEST SOURCE OF BACKEND LOAD.
 *
 * Every open tab hits /notifications/get_summary on this interval whether or
 * not anyone is touching it, so this one constant sets a floor on traffic that
 * user activity has nothing to do with. At 25s that was 144 requests/hour per
 * tab -- roughly 40 req/s from a thousand concurrent tabs, which measured as
 * more load than everything sellers actually click, combined.
 *
 * 60s costs a badge at most ~35 extra seconds of staleness and cuts that floor
 * by 2.4x. If real-time ever matters more than it does today, the answer is
 * server-sent events, not a smaller number here.
 *
 * A hidden tab does not poll at all: it refreshes once when it is shown again.
 * And a poll that brings back exactly the last summary changes no state, so the
 * bell, the sidebar and every list row reading this context do not re-render
 * every minute for nothing.
 */
const POLL_INTERVAL_MS = 60000;

// Persisted "seen" baseline for the bell's badge count only — dismissing the
// badge (opening the bell) never touches is_read server-side, it just hides
// the number until the true unread total grows past this baseline again.
// Kept in localStorage so a page reload doesn't undo the dismissal, and
// wiped for free whenever the app does clearSession() on logout.
const LAST_SEEN_KEY = "notif_badge_last_seen_total";

const EMPTY_BREAKDOWN = {
  // Store integrations. notifications.entity_type is varchar, not an ENUM, so
  // these needed no schema change — only a key here to light up a badge.
  //
  // The names must match what log_activity() is actually CALLED with, not what the
  // sync log uses: `sales_channel` is the notification vocabulary, while `channel`
  // is ChannelSyncLog's own entity_type in a different table entirely. A key of
  // `channel` here would sit at 0 for ever, because nothing writes a notification
  // under it.
  sales_channel: 0,
  channel_order: 0,
  product_link: 0,
  listing_draft: 0,
  channel_job: 0,
  channel_config: 0,
  order_tracking: 0,
  listing_stock: 0,
  order: 0, sourcing_request: 0, restock_request: 0, product: 0, client: 0,
  wallet: 0, subscription: 0, credits: 0, account: 0, top_up_request: 0,
  cancellation_request: 0, deletion_request: 0,
};

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  // Ordinary notifications only — what the bell counts and lists. Connected-store
  // messages are counted apart in storeAlertsUnread (the homepage's Store alerts
  // section); unreadByEntityType still breaks BOTH down, for the sidebar badges.
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [storeAlertsUnread, setStoreAlertsUnread] = useState(0);
  const [unreadByEntityType, setUnreadByEntityType] = useState(EMPTY_BREAKDOWN);
  // entity_type -> { [entity_id]: true } for unread notifications — lets
  // list-page rows (orders, requests, products...) show a small "recently
  // updated" dot without paginating the full notifications list.
  const [unreadEntityMap, setUnreadEntityMap] = useState({});
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSeenTotal, setLastSeenTotal] = useState(() => {
    const stored = Number(localStorage.getItem(LAST_SEEN_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : 0;
  });
  const intervalRef = useRef(null);
  const lastSummaryRef = useRef("");

  // Never let the seen-baseline sit above the true unread count — otherwise
  // a notification read elsewhere (or via mark-all) would mask a later,
  // genuinely new notification behind the stale higher baseline. Accepts
  // either a value or a setState-style updater, mirroring setUnreadTotal's
  // own call sites below. This is also what re-bases a baseline stored while the
  // bell still counted store messages: the first summary lowers it on arrival.
  const applyUnreadTotal = useCallback((totalOrUpdater) => {
    setUnreadTotal((prevTotal) => {
      const total = typeof totalOrUpdater === "function" ? totalOrUpdater(prevTotal) : totalOrUpdater;
      setLastSeenTotal((prevSeen) => {
        if (prevSeen <= total) return prevSeen;
        localStorage.setItem(LAST_SEEN_KEY, String(total));
        return total;
      });
      return total;
    });
  }, []);

  // One unread notification of `entityType` was read (or `count` of them): take
  // it off whichever total it belonged to.
  const decrementTotals = useCallback((entityType, count = 1) => {
    if (isStoreAlert(entityType)) {
      setStoreAlertsUnread((prev) => Math.max(0, prev - count));
    } else {
      applyUnreadTotal((prev) => Math.max(0, prev - count));
    }
  }, [applyUnreadTotal]);

  const dismissBadge = useCallback(() => {
    setLastSeenTotal(unreadTotal);
    localStorage.setItem(LAST_SEEN_KEY, String(unreadTotal));
  }, [unreadTotal]);

  // { poll: true } comes from the timer only. Every other refresh (after a
  // mark-as-read, or asked for by a page) applies the answer even when it is
  // unchanged, so an optimistic update that the server refused is put right.
  const refresh = useCallback((options) => {
    if (!localStorage.token) return;
    const isPoll = Boolean(options && options.poll);
    getSummary()
      .then((payload) => {
        if (!payload) return;
        const summary = JSON.stringify(payload);
        if (isPoll && summary === lastSummaryRef.current) return;
        lastSummaryRef.current = summary;
        applyUnreadTotal(payload.unread_total || 0);
        setStoreAlertsUnread(payload.store_alerts_unread || 0);
        setUnreadByEntityType({ ...EMPTY_BREAKDOWN, ...(payload.unread_by_entity_type || {}) });
        setUnreadEntityMap(payload.unread_entity_ids || {});
        setRecent(payload.recent || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [applyUnreadTotal]);

  useEffect(() => {
    if (!localStorage.token) {
      setLoading(false);
      return;
    }

    refresh();
    intervalRef.current = setInterval(() => {
      if (!localStorage.token) {
        clearInterval(intervalRef.current);
        return;
      }
      if (document.hidden) return;
      refresh({ poll: true });
    }, POLL_INTERVAL_MS);

    // Back to the tab: catch up at once rather than at the next tick.
    const onVisible = () => {
      if (!document.hidden && localStorage.token) refresh({ poll: true });
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const markAsRead = useCallback((id, entityType, entityId) => {
    setRecent((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    decrementTotals(entityType);
    if (entityType) {
      setUnreadByEntityType((prev) => ({ ...prev, [entityType]: Math.max(0, (prev[entityType] || 0) - 1) }));
    }
    if (entityType && entityId !== undefined && entityId !== null) {
      const key = String(entityId);
      setUnreadEntityMap((prev) => {
        if (!prev[entityType]?.[key]) return prev;
        const { [key]: _removed, ...rest } = prev[entityType];
        return { ...prev, [entityType]: rest };
      });
    }
    // Then re-read, as the other mark-read calls do: a failed call used to
    // leave the dot and the count dropped here until the next poll, and a
    // stale poll answer could bring the count back while this one was on its
    // way.
    apiMarkRead(id).catch(() => {}).finally(() => setTimeout(refresh, 300));
  }, [decrementTotals, refresh]);

  // With an entity type: that type only (the store pages clear their own this
  // way). Without one: every ORDINARY notification — the bell's and the
  // Notifications page's "Mark all as read" must leave the Store alerts section,
  // which they never showed, alone. The server applies the same split.
  const markAllAsRead = useCallback((entityType) => {
    setRecent((prev) => prev.map((n) => (!entityType || n.entity_type === entityType) ? { ...n, is_read: true } : n));
    setUnreadByEntityType((prev) => {
      if (entityType) {
        decrementTotals(entityType, prev[entityType] || 0);
        return { ...prev, [entityType]: 0 };
      }
      applyUnreadTotal(0);
      return Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, isStoreAlert(k) ? v : 0]));
    });
    setUnreadEntityMap((prev) => (
      entityType
        ? { ...prev, [entityType]: {} }
        : Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, isStoreAlert(k) ? v : {}]))
    ));
    apiMarkAllRead(entityType).catch(() => {}).finally(() => setTimeout(refresh, 300));
  }, [refresh, applyUnreadTotal, decrementTotals]);

  // The homepage's Store alerts "Mark all as read".
  const markAllStoreAlertsRead = useCallback(() => {
    setStoreAlertsUnread(0);
    setUnreadByEntityType((prev) => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, isStoreAlert(k) ? 0 : v])));
    setUnreadEntityMap((prev) => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, isStoreAlert(k) ? {} : v])));
    return apiMarkAllRead(undefined, "store").catch(() => {}).finally(() => setTimeout(refresh, 300));
  }, [refresh]);

  // One grouped Store alert (every repeat of it) was opened. `alert.unread` is how
  // many of its rows were still unread, so the counts drop by the right amount at
  // once; the server clears the group by the newest row's kind + wording.
  const markStoreAlertRead = useCallback((alert) => {
    const { id, entity_type: entityType, unread } = alert || {};
    if (!unread || !STORE_ALERT_ENTITY_TYPES.has(entityType)) return Promise.resolve();
    decrementTotals(entityType, unread);
    setUnreadByEntityType((prev) => ({ ...prev, [entityType]: Math.max(0, (prev[entityType] || 0) - unread) }));
    return apiMarkStoreAlertGroupRead(id).catch(() => {}).finally(() => setTimeout(refresh, 300));
  }, [decrementTotals, refresh]);

  // Marks every unread notification about one entity (e.g. one order) as
  // read in a single call — used by list-row hover. Never touches other
  // notifications about the same entity_type, unlike markAllAsRead.
  const markEntityRead = useCallback((entityType, entityId) => {
    const key = String(entityId);
    if (!unreadEntityMap[entityType]?.[key]) return;

    setUnreadEntityMap((prev) => {
      const { [key]: _removed, ...rest } = prev[entityType] || {};
      return { ...prev, [entityType]: rest };
    });
    decrementTotals(entityType);
    setUnreadByEntityType((prev) => ({ ...prev, [entityType]: Math.max(0, (prev[entityType] || 0) - 1) }));
    apiMarkReadByEntity(entityType, entityId).catch(() => {}).finally(() => setTimeout(refresh, 300));
  }, [unreadEntityMap, decrementTotals, refresh]);

  const badgeCount = Math.max(0, unreadTotal - lastSeenTotal);

  const value = useMemo(() => ({
    unreadTotal, badgeCount, storeAlertsUnread, unreadByEntityType, unreadEntityMap, recent, loading,
    markAsRead, markAllAsRead, markEntityRead, markAllStoreAlertsRead, markStoreAlertRead, refresh, dismissBadge,
  }), [
    unreadTotal, badgeCount, storeAlertsUnread, unreadByEntityType, unreadEntityMap, recent, loading,
    markAsRead, markAllAsRead, markEntityRead, markAllStoreAlertsRead, markStoreAlertRead, refresh, dismissBadge,
  ]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within a NotificationsProvider");
  return ctx;
}
